import _ from 'underscore';
import config from 'config';
import axios from 'axios';
import { parse as urlParse, format as urlFormat } from 'url';
import {
    parse as querystringParse,
    stringify as querystringStringify
} from 'querystring';
import { strictEqual as assertStrictEqual, ok as assertOk } from 'assert';
import { getAccessToken } from './AuthToken';

import BootstrapData from 'models/BootstrapData';
const event = BootstrapData('bootstrap-data-event');
const eventUniqueName = event['unique-name'];
import {isBizzaboInternalWebsiteWhiteLabeled} from '@bizzabo/whitelabel-utils';


export const getDomainAdjustedPath = (path) => {
    if (isBizzaboInternalWebsiteWhiteLabeled(event)) {
        if (!event.onlyEventDomain) {
            return `${event.whitelabelPath}${path}`;
        }
        return path;
    }
    return `${eventUniqueName}${path}`;
};



let noCacheValue = Date.now();

const apiServer = config.apiServer;
const http = axios.create({
    baseURL: apiServer
});

export const getHttpForRefreshToken = () => axios.create({ baseURL: '/' });

const addNoCache = inputUrl => {
    const url = urlParse(inputUrl);
    const parsedQuery = querystringParse(url.query || '');
    parsedQuery._nocache = (noCacheValue++).toString();
    url.search = querystringStringify(parsedQuery);
    return urlFormat(url);
};

const allowedMethods = new Set([
    'get',
    'post',
    'put',
    'delete',
    'patch',
    'options',
    'head'
]);

let authTokenRequestInstance = null;

const resetGetAccessTokenRequest = () => {
    authTokenRequestInstance = null;
};

export const authTokenRequest = async(
    {
        url = getDomainAdjustedPath('/userProfile/oauth/refreshToken'),
        httpForRefreshToken = getHttpForRefreshToken()
    } = {}) => {
    try {
        return await httpForRefreshToken({
            method: 'post',
            url,
            headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json'
            },
            responseType: 'json'
        });
    } finally {
        resetGetAccessTokenRequest();
    }
};

export const getNewAccessToken = async(
    {
        url,
        httpForRefreshToken
    } = {}
) => {

    if (authTokenRequestInstance) {
        return authTokenRequestInstance;
    }

    authTokenRequestInstance = authTokenRequest({ url, httpForRefreshToken });
    return authTokenRequestInstance;
};

export const retryAjax = async({ ajaxCall, refreshTokenPath, httpForRefreshToken }) => {
    await getNewAccessToken({ url: refreshTokenPath, httpForRefreshToken });
    try {
        const isRetry = true;
        const response = await ajaxCall(isRetry);
        return response;
    } catch (e) {
        resetGetAccessTokenRequest();
        return e;
    }
};

export const refreshWrapper = ({ ajaxCall, refreshTokenPath, httpForRefreshToken }) => (async() => {
    try {
        const response = await ajaxCall();
        return response;
    } catch (e) {
        if (e.status === 401) {
            const response = await retryAjax({ ajaxCall, refreshTokenPath, httpForRefreshToken });
            return response;
        } else {
            return e;
        }
    }
})();

const parseIntoInputData = data => {
    if (!data) {
        return {};
    }
    if (data && _.isString(data)) {
        return JSON.parse(data);
    }
    return data;
};

const bizzaboAjax = function({
    method,
    type,
    headers = {},
    url,
    refreshTokenPath,
    httpForRefreshToken,
    data,
    params,
    success,
    error
}) {
    const httpMethod = (method || type || 'get').toLowerCase();
    assertStrictEqual(typeof url, 'string', 'options.url must be a string');
    assertOk(
        allowedMethods.has(httpMethod),
        `options.method of options.type must be one of HTTP methods, ${httpMethod} given`
    );
    const ajaxCall = async(isRetry) => {
        const defaultHeaders = {
            'Content-Type': 'application/json',
            Accept: 'application/json'
        };

        const accessToken = getAccessToken();
        if (accessToken) {
            defaultHeaders.Authorization = 'Bearer ' + accessToken;
        }

        const inputData = parseIntoInputData(data);
        const inputParams = params || {};

        const axiosData = httpMethod === 'get' ? null : inputData;
        const axiosParams =
            httpMethod === 'get' ? { ...inputData, ...inputParams } : inputParams;


        try {
            const response = await http({
                method: httpMethod,
                url: addNoCache(url),
                data: axiosData,
                params: axiosParams,
                headers: { ...defaultHeaders, ...headers },
                withCredentials: true,
            });
            const { data, status, headers } = response;
            try {
                success && success(data, status, headers);
            } catch (e) {
                console.error('Error in success handler', e, e.stack);
                throw e;
            }
            return data;
        } catch (errorResponse) {
            const { response: { data, status } = {} } = errorResponse || {};
            if (status !== 401 || isRetry) {
                error && error(data || {}, status || 503);
            }
            return Promise.reject({ data, status });
        }
    };
    return refreshWrapper(
        {
            refreshTokenPath,
            httpForRefreshToken,
            ajaxCall
        }
    );
};

export default bizzaboAjax;
